import { useEffect, useContext, useState, useCallback, useMemo } from 'react'
import { startCase, toLower } from 'lodash'
import { Grid } from '@mui/material'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import BatchBillsGrid from 'components/containers/batch-bills/BatchBillsGrid'
import { useGetBatchBillsQuery } from 'services/batchBills'
import { dateUnixFormatter } from 'utils/data-manipulation/valueFormatter'
import { BILL_STATUSES, CURRENCY_PREPENDS, SORTABLE_BATCH_BILL_FIELDS, PAGE_SIZE } from 'utils/constants'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import { useSelectFilter } from 'utils/custom-hooks/useSelectFilter'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'

const BatchBillsContainer = () => {
  const context = useContext(ContentLayoutContext)

  const { allowedOptions, renderSelectFilter } = useSelectFilter({
    localStorageKey: 'batchBillStatuses',
    options: [BILL_STATUSES.DUE, BILL_STATUSES.PAID, BILL_STATUSES.SAVING],
    defaultOptions: [BILL_STATUSES.DUE.id, BILL_STATUSES.PAID.id],
    label: 'Status',
  })

  const [filter, setFilter] = useState({
    sort: {
      field: SORTABLE_BATCH_BILL_FIELDS.EXTERNAL_ID,
      ascending: false,
    },
    statuses: allowedOptions,
    search: '',
  })

  const [paginationOptions, setPaginationOptions] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
  })

  const { data, isFetching } = useGetBatchBillsQuery({
    filter: JSON.stringify(filter),
    ...paginationOptions,
  })

  useGlobalLoader(false)

  const { defaultCurrency } = useAppSelector(state => state.settings)
  const { currencyExchangeRatesFF } = useAppSelector(state => state.featureFlags?.variables || {})

  useEffect(() => {
    context.setContainerTitle('Purchases - Batch Bills')
  }, [])

  useEffect(() => {
    setFilter(prevFilter => ({
      ...prevFilter,
      statuses: allowedOptions,
    }))
  }, [allowedOptions])

  const transformedRows = useMemo(
    () =>
      (data?.Items || []).slice().map(batchBill => {
        const batchBillCurrency = batchBill?.currency
        const batchBillPricePrepend =
          !batchBillCurrency || defaultCurrency === batchBillCurrency
            ? CURRENCY_PREPENDS[defaultCurrency]?.hc
            : CURRENCY_PREPENDS[batchBillCurrency]?.fc
        return {
          id: batchBill.id,
          [SORTABLE_BATCH_BILL_FIELDS.EXTERNAL_ID]: batchBill?.externalId,
          batchBillLink: `/app/purchases/batch-bills/${batchBill.id}`,
          status: BILL_STATUSES[batchBill?.status?.toUpperCase()]?.name || startCase(toLower(batchBill?.status || '')),
          billCount: batchBill?.bills?.length || 0,
          batchBillTotal: batchBill?.billTotal,
          paid: batchBill?.paymentsTotal,
          currency: batchBill?.currency,
          [SORTABLE_BATCH_BILL_FIELDS.LAST_UPDATED]: dateUnixFormatter(batchBill?.updatedAt),
          pricePrepend: currencyExchangeRatesFF ? batchBillPricePrepend : '$',
        }
      }),
    [data, defaultCurrency, currencyExchangeRatesFF],
  )

  const handleSortModelChange = useCallback(sortModel => {
    setFilter(prevFilter => ({
      ...prevFilter,
      sort: {
        field: sortModel?.[0]?.field,
        ascending: sortModel?.[0]?.sort === 'asc',
      },
    }))
  }, [])

  return (
    <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
      <BatchBillsGrid
        rows={transformedRows}
        loading={isFetching}
        filter={filter}
        setFilter={setFilter}
        useBackendSortAndFilter
        disableColumnMenu
        initialState={{
          sorting: {
            sortModel: [{ field: SORTABLE_BATCH_BILL_FIELDS.EXTERNAL_ID, sort: 'desc' }],
          },
        }}
        // pagination
        rowCount={data?.totalResultsCount || 0}
        paginationMode="server"
        onPageChange={newPageNumber => setPaginationOptions(prevState => ({ ...prevState, page: newPageNumber + 1 }))}
        onPageSizeChange={newPageSize => setPaginationOptions(prevState => ({ ...prevState, pageSize: newPageSize }))}
        autoPageSize={false}
        pageSize={paginationOptions.pageSize}
        // sorting
        sortingMode="server"
        sortingOrder={['asc', 'desc']}
        onSortModelChange={handleSortModelChange}
        // search
        allowedOptions={allowedOptions}
        renderSelectFilter={renderSelectFilter}
      />
    </Grid>
  )
}

export default BatchBillsContainer
